import type { components } from '@sportik/api-coach/schema';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { browserTimeZone, lastWeeks, today } from '@/lib/dates';
import {
  DEFAULT_STATS_GROUPING,
  DEFAULT_STATS_RANGE,
  type StatsGrouping,
  statsRange,
} from '@/lib/stats-range';
import type { ViewRange } from '@/lib/view-range';
import { api } from '@/services/api-client';

type StatsMetric = components['schemas']['StatsSeries']['metric'];

/** The metric the Statistics tab opens on, which entering the page prefetches. */
export const DEFAULT_STATS_METRIC = 'sets_per_muscle' satisfies StatsMetric;

/** How many weeks the Overview's bars cover: three months, this week included. */
const WEEKS = 13;

/**
 * The one definition of this read: duration, volume, and sets per week over
 * the last thirteen weeks, the current one last.
 */
export function weeklyTotalsOptions(clientId: string) {
  return api.queryOptions('get', '/clients/{clientId}/stats', {
    params: {
      path: { clientId },
      query: {
        metric: ['duration', 'volume', 'sets'],
        grouping: 'week',
        ...lastWeeks(today(), WEEKS),
        timeZone: browserTimeZone(),
      },
    },
  });
}

/** The client's training per week: one series each for duration, volume, and sets. */
export function useWeeklyTotals(clientId: string) {
  return useQuery(weeklyTotalsOptions(clientId));
}

/**
 * The one definition of this read: one metric in buckets over a range that
 * ends with the current bucket. The defaults are the Statistics tab's first
 * view. A change of range or grouping keeps the series shown until the new
 * one arrives.
 */
export function statsOptions(
  clientId: string,
  metric: StatsMetric = DEFAULT_STATS_METRIC,
  grouping: StatsGrouping = DEFAULT_STATS_GROUPING,
  range: ViewRange = DEFAULT_STATS_RANGE,
) {
  return {
    ...api.queryOptions('get', '/clients/{clientId}/stats', {
      params: {
        path: { clientId },
        query: {
          metric: [metric],
          grouping,
          ...statsRange(range, grouping, today()),
          timeZone: browserTimeZone(),
        },
      },
    }),
    placeholderData: keepPreviousData,
  };
}

/** One statistics metric of the client, bucketed over a range. */
export function useStats(
  clientId: string,
  metric: StatsMetric,
  grouping: StatsGrouping,
  range: ViewRange,
) {
  return useQuery(statsOptions(clientId, metric, grouping, range));
}

/** The one definition of this read: how the current week went against the plan. */
export function adherenceOptions(clientId: string) {
  return api.queryOptions('get', '/clients/{clientId}/adherence', {
    params: {
      path: { clientId },
      query: { week: today(), timeZone: browserTimeZone() },
    },
  });
}

/** This week's sessions done of planned, sets followed, and prescribed load. */
export function useAdherence(clientId: string) {
  return useQuery(adherenceOptions(clientId));
}
